import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { ChevronDown, ChevronUp } from 'lucide-react';

const countWords = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;

const WordCounter = ({ text, max, testId }) => {
  const n = countWords(text);
  const over = n > max;
  return (
    <p
      className={`text-xs mt-1 ${over ? 'text-red-600 font-semibold' : 'text-gray-400'}`}
      data-testid={testId}
    >
      {n} / {max} words{over ? ' — over limit' : ''}
    </p>
  );
};

/**
 * Extracted from IncidentsPage to prevent focus-loss bug caused by defining
 * the form as a nested component inside the parent (which was remounting on
 * every keystroke). Kept as a stable top-level component.
 */
export const IncidentForm = ({
  form,
  updateForm,
  vehicles,
  drivers,
  incidentTypes,
  showThirdParty,
  setShowThirdParty,
  saving,
  onSubmit,
  onCancel,
  isEdit = false,
}) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Vehicle *</Label>
        <Select value={form.vehicle_id} onValueChange={v => updateForm('vehicle_id', v)} disabled={isEdit}>
          <SelectTrigger data-testid="incident-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
          <SelectContent>
            {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.name} ({v.registration})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Date *</Label>
        <Input type="date" value={form.date} onChange={e => updateForm('date', e.target.value)} data-testid="incident-date-input" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Incident Type *</Label>
        <Select value={form.incident_type} onValueChange={v => updateForm('incident_type', v)}>
          <SelectTrigger data-testid="incident-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {incidentTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Location <span className="text-gray-400 text-xs">(max 30 words)</span></Label>
        <Input
          value={form.location}
          onChange={e => updateForm('location', e.target.value)}
          placeholder="e.g., N1 Highway, Cape Town"
          data-testid="incident-location-input"
        />
        <WordCounter text={form.location} max={30} testId="incident-location-words" />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Description * <span className="text-gray-400 text-xs">(up to 500 words)</span></Label>
      <Textarea
        value={form.description}
        onChange={e => updateForm('description', e.target.value)}
        placeholder="Describe what happened..."
        rows={6}
        data-testid="incident-description-input"
      />
      <WordCounter text={form.description} max={500} testId="incident-description-words" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Driver <span className="text-red-500">*</span></Label>
        {drivers.length === 0 ? (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            No drivers on record. <a href="/drivers" className="underline font-medium">Add a driver first</a> before logging an incident — every incident must be attributable to a driver.
          </div>
        ) : (
          <Select value={form.driver_id} onValueChange={v => updateForm('driver_id', v)}>
            <SelectTrigger data-testid="incident-driver-select"><SelectValue placeholder="Select driver at fault" /></SelectTrigger>
            <SelectContent>
              {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}{d.license_number ? ` — ${d.license_number}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="space-y-2">
        <Label>Estimated Repair Cost (R) *</Label>
        <Input type="number" value={form.damage_estimate} onChange={e => updateForm('damage_estimate', e.target.value)} placeholder="0.00" className="font-data" step="0.01" min="0" data-testid="incident-cost-input" required />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Damage Description <span className="text-gray-400 text-xs">(up to 500 words)</span></Label>
      <Textarea
        value={form.damage_description}
        onChange={e => updateForm('damage_description', e.target.value)}
        placeholder="Describe the damage..."
        rows={5}
        data-testid="incident-damage-input"
      />
      <WordCounter text={form.damage_description} max={500} testId="incident-damage-words" />
    </div>
    <div className="border rounded-md overflow-hidden">
      <button type="button" className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={() => setShowThirdParty(p => !p)}>
        <span>Third Party Details (optional)</span>
        {showThirdParty ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {showThirdParty && (
        <div className="p-4 grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={form.third_party_name} onChange={e => updateForm('third_party_name', e.target.value)} placeholder="Third party name" />
          </div>
          <div className="space-y-2">
            <Label>Contact</Label>
            <Input value={form.third_party_contact} onChange={e => updateForm('third_party_contact', e.target.value)} placeholder="Phone number" />
          </div>
          <div className="space-y-2">
            <Label>Vehicle Reg</Label>
            <Input value={form.third_party_vehicle_reg} onChange={e => updateForm('third_party_vehicle_reg', e.target.value)} placeholder="ABC 123 GP" className="font-data" />
          </div>
        </div>
      )}
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Police Case Number</Label>
        <Input value={form.police_case_number} onChange={e => updateForm('police_case_number', e.target.value)} placeholder="CAS 123/02/2026" className="font-data" data-testid="incident-case-input" />
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select value={form.status} onValueChange={v => updateForm('status', v)}>
          <SelectTrigger data-testid="incident-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    {(form.status === 'closed' || form.status === 'in_progress') && (
      <div className="space-y-2">
        <Label>Resolution Notes</Label>
        <Textarea value={form.resolution_notes} onChange={e => updateForm('resolution_notes', e.target.value)} placeholder="How was this resolved?" rows={2} data-testid="resolution-notes-input" />
      </div>
    )}
    <div className="flex justify-end gap-3 pt-2">
      <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      <Button type="submit" disabled={saving} className="bg-gray-800 hover:bg-gray-700" data-testid="save-incident-btn">
        {saving ? 'Saving...' : isEdit ? 'Update Incident' : 'Record Incident'}
      </Button>
    </div>
  </form>
);
